import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { usePartnerRole } from "./usePartnerRole";
import { parseError } from "@/lib/error-utils";

export interface PartnerOrg {
  id: string;
  name: string;
  cnpj: string | null;
  telefone: string | null;
  email_contato: string | null;
  plan_type: string;
  enabled_modules: string[];
  partner_id: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  // Asaas fields
  asaas_customer_id?: string | null;
  asaas_subscription_id?: string | null;
  asaas_subscription_status?: string | null;
  asaas_next_due_date?: string | null;
  asaas_last_payment_status?: string | null;
}

export interface PartnerOrgWithStats extends PartnerOrg {
  users_count: number;
  leads_count: number;
}

// Planos fechados - sem personalizado
import { PLAN_PRESETS, PlanType } from "@/hooks/useOrgModules";

export interface CreateOrgData {
  name: string;
  cnpj?: string;
  endereco?: string;
  telefone?: string;
  email_contato?: string;
  plan_type?: PlanType;
  enabled_modules?: string[]; // Módulos personalizados (opcional)
  admin_name?: string;
  admin_email?: string;
  admin_password?: string;
}

export interface CreateUserData {
  org_id: string;
  email: string;
  senha: string;
  nome: string;
  role: 'proprietario' | 'gerente' | 'vendedor';
}

export function usePartnerOrgs() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { partnerId, partnerData } = usePartnerRole();

  // Fetch partner's organizations
  // Get member info for org filtering
  const { memberInfo, isPartnerOwner } = usePartnerRole();
  const allowedOrgIds = memberInfo?.allowed_org_ids;

  const { data: orgs, isLoading, error } = useQuery({
    queryKey: ["partner-orgs", partnerId, allowedOrgIds],
    queryFn: async () => {
      if (!partnerId) return [];

      const { data, error } = await supabase
        .from("orgs")
        .select("*")
        .eq("partner_id", partnerId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Filter by allowed_org_ids if member has restrictions (NULL = all orgs)
      // Owners always see all orgs
      if (!isPartnerOwner && allowedOrgIds && allowedOrgIds.length > 0) {
        return (data as PartnerOrg[]).filter(org => allowedOrgIds.includes(org.id));
      }
      
      return data as PartnerOrg[];
    },
    enabled: !!partnerId,
  });

  // Fetch orgs with stats only when needed (lazy load)
  const { data: orgsWithStats } = useQuery({
    queryKey: ["partner-orgs-with-stats", partnerId],
    queryFn: async () => {
      if (!partnerId || !orgs || orgs.length === 0) return [];

      // Batch queries instead of Promise.all for each org
      const orgIds = orgs.map(o => o.id);
      
      // Get all user counts in one query
      const { data: userCounts } = await supabase
        .from("org_members")
        .select("org_id")
        .in("org_id", orgIds);
      
      // Get all lead counts in one query
      const { data: leadCounts } = await supabase
        .from("leads")
        .select("org_id")
        .in("org_id", orgIds);

      // Aggregate counts
      const userCountMap = (userCounts || []).reduce((acc, item) => {
        acc[item.org_id] = (acc[item.org_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const leadCountMap = (leadCounts || []).reduce((acc, item) => {
        acc[item.org_id] = (acc[item.org_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return orgs.map(org => ({
        ...org,
        enabled_modules: Array.isArray(org.enabled_modules) ? org.enabled_modules : [],
        users_count: userCountMap[org.id] || 0,
        leads_count: leadCountMap[org.id] || 0,
      } as PartnerOrgWithStats));
    },
    enabled: !!partnerId && !!orgs && orgs.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Create org via edge function
  const createOrg = useMutation({
    mutationFn: async (data: CreateOrgData) => {
      const { data: result, error } = await supabase.functions.invoke("partner-create-org", {
        body: data,
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["partner-orgs"] });
      queryClient.invalidateQueries({ queryKey: ["partner-orgs-with-stats"] });
      toast({ 
        title: "Organização criada com sucesso!",
        description: result.orgs_remaining !== undefined 
          ? `Você ainda pode criar ${result.orgs_remaining} organização(ões).`
          : undefined
      });
    },
    onError: (error: Error) => {
      const parsed = parseError(error);
      toast({
        title: "Erro ao criar organização",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
    },
  });

  // Create user via edge function
  const createUser = useMutation({
    mutationFn: async (data: CreateUserData) => {
      const { data: result, error } = await supabase.functions.invoke("partner-create-user", {
        body: data,
      });

      if (error) throw error;
      if (result?.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-orgs-with-stats"] });
      toast({ title: "Usuário criado com sucesso!" });
    },
    onError: (error: Error) => {
      const parsed = parseError(error);
      toast({
        title: "Erro ao criar usuário",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
    },
  });

  // Update org - agora permite enabled_modules personalizados
  const updateOrg = useMutation({
    mutationFn: async ({ id, plan_type, enabled_modules, ...data }: Partial<PartnerOrg> & { id: string }) => {
      // Only allow updating specific fields
      const allowedFields = ['name', 'cnpj', 'telefone', 'email_contato'];
      const filteredData: Record<string, any> = Object.fromEntries(
        Object.entries(data).filter(([key]) => allowedFields.includes(key))
      );

      // Se plan_type foi alterado, aplicar
      if (plan_type && ['basico', 'medio', 'completo'].includes(plan_type)) {
        filteredData.plan_type = plan_type;
        // Se enabled_modules customizado foi fornecido, usar; senão, usar preset
        if (enabled_modules && Array.isArray(enabled_modules) && enabled_modules.length > 0) {
          filteredData.enabled_modules = enabled_modules;
        } else {
          filteredData.enabled_modules = PLAN_PRESETS[plan_type as PlanType];
        }
      } else if (enabled_modules && Array.isArray(enabled_modules) && enabled_modules.length > 0) {
        // Apenas atualizar enabled_modules se fornecido
        filteredData.enabled_modules = enabled_modules;
      }

      const { error } = await supabase
        .from("orgs")
        .update(filteredData)
        .eq("id", id)
        .eq("partner_id", partnerId); // Extra safety

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-orgs"] });
      queryClient.invalidateQueries({ queryKey: ["partner-orgs-with-stats"] });
      toast({ title: "Organização atualizada!" });
    },
    onError: (error: Error) => {
      const parsed = parseError(error);
      toast({
        title: "Erro ao atualizar organização",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
    },
  });

  // Delete org mutation
  const deleteOrg = useMutation({
    mutationFn: async (orgId: string) => {
      // First, delete all org_members
      const { error: membersError } = await supabase
        .from("org_members")
        .delete()
        .eq("org_id", orgId);

      if (membersError) {
        console.error("Error deleting org_members:", membersError);
        throw new Error("Erro ao remover membros da organização");
      }

      // Then delete the org itself
      const { error: orgError } = await supabase
        .from("orgs")
        .delete()
        .eq("id", orgId)
        .eq("partner_id", partnerId); // Safety: only delete if belongs to this partner

      if (orgError) {
        console.error("Error deleting org:", orgError);
        throw orgError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-orgs"] });
      queryClient.invalidateQueries({ queryKey: ["partner-orgs-with-stats"] });
      toast({ title: "Organização excluída com sucesso!" });
    },
    onError: (error: Error) => {
      const parsed = parseError(error);
      toast({
        title: "Erro ao excluir organização",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
    },
  });

  // Toggle org active status
  const toggleOrgActive = useMutation({
    mutationFn: async ({ orgId, isActive }: { orgId: string; isActive: boolean }) => {
      const { data, error } = await supabase
        .from("orgs")
        .update({ is_active: isActive })
        .eq("id", orgId)
        .eq("partner_id", partnerId) // Safety: only update if belongs to this partner
        .select("id, is_active")
        .single();

      if (error) throw error;
      if (!data) throw new Error("Organização não encontrada ou sem permissão");
      
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["partner-orgs"] });
      queryClient.invalidateQueries({ queryKey: ["partner-orgs-with-stats"] });
      toast({ 
        title: variables.isActive ? "Organização ativada!" : "Organização desativada!",
        description: variables.isActive 
          ? "Usuários podem acessar novamente."
          : "Usuários desta organização não conseguirão acessar a plataforma."
      });
    },
    onError: (error: Error) => {
      const parsed = parseError(error);
      toast({
        title: "Erro ao alterar status",
        description: `${parsed.message}${parsed.suggestion ? ` - ${parsed.suggestion}` : ''}`,
        variant: "destructive",
      });
    },
  });

  // Calculate remaining orgs
  const orgsRemaining = partnerData 
    ? partnerData.max_orgs - (orgs?.length || 0)
    : 0;

  return {
    orgs,
    orgsWithStats,
    isLoading,
    error,
    createOrg,
    createUser,
    updateOrg,
    deleteOrg,
    toggleOrgActive,
    orgsRemaining,
    maxOrgs: partnerData?.max_orgs || 0,
  };
}
